import React, { useState, useEffect } from 'react';
import { Plus, Clock } from 'lucide-react';
import { CANNED_UPDATES } from '../../config/cannedUpdates';

const RecentUpdates = ({ clientId, backendUrl, adminKey, onUpdate }) => {
  const [updates, setUpdates] = useState([]);
  const [selectedCanned, setSelectedCanned] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUpdates = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/updates`);
      if (response.ok) {
        const data = await response.json();
        setUpdates(data);
      }
    } catch (err) {
      console.error('Error fetching updates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchUpdates();
    }
  }, [clientId]);

  const handleAddUpdate = async () => {
    // Determine the message to send
    const message = customMessage.trim() || selectedCanned;
    
    if (!message) {
      alert('Please select a canned update or write a custom message');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/updates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ message })
      });

      if (response.ok) {
        // Clear form
        setSelectedCanned('');
        setCustomMessage('');
        
        // Refresh updates list
        await fetchUpdates();
        
        // Notify parent to refresh if needed
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        alert(`Failed to add update: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding update:', err);
      alert('Failed to add update');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #E2E8F0'
    }}>
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '16px', 
        fontWeight: '700', 
        color: '#1E293B',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Clock size={20} color="#667eea" />
        Recent Updates (Client-Facing)
      </h3>

      {/* Add Update Form */}
      <div style={{ marginBottom: '20px', padding: '16px', background: '#F8FAFC', borderRadius: '8px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#475569', 
            marginBottom: '6px' 
          }}>
            Quick Select (Canned Updates)
          </label>
          <select
            value={selectedCanned}
            onChange={(e) => setSelectedCanned(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: '#fff'
            }}
          >
            <option value="">-- Select a canned update --</option>
            {CANNED_UPDATES.map((update, idx) => (
              <option key={idx} value={update}>
                {update}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#475569', 
            marginBottom: '6px' 
          }}>
            Write a custom update (optional)
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Enter your custom update message..."
            rows="3"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
          <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
            {customMessage.trim() ? 'Custom message will be used' : selectedCanned ? 'Canned message will be used' : 'Select or write a message'}
          </div>
        </div>

        <button
          onClick={handleAddUpdate}
          disabled={submitting || (!selectedCanned && !customMessage.trim())}
          style={{
            width: '100%',
            padding: '10px',
            background: submitting || (!selectedCanned && !customMessage.trim()) ? '#CBD5E1' : '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: submitting || (!selectedCanned && !customMessage.trim()) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background 0.2s'
          }}
        >
          <Plus size={16} />
          {submitting ? 'Adding...' : 'Add Update'}
        </button>
      </div>

      {/* Updates List */}
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#64748B', marginBottom: '12px' }}>
          Update History ({updates.length})
        </h4>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94A3B8' }}>
            Loading updates...
          </div>
        ) : updates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94A3B8', fontSize: '14px' }}>
            No updates yet. Add one above!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {updates.map((update) => (
              <div
                key={update.id}
                style={{
                  padding: '12px',
                  background: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }}
              >
                <div style={{ fontSize: '14px', color: '#1E293B', marginBottom: '6px' }}>
                  {update.message}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#64748B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Clock size={12} />
                  {formatDateTime(update.created_at)}
                  <span style={{ 
                    padding: '2px 6px', 
                    background: '#667eea', 
                    color: '#fff', 
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    Staff
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentUpdates;
